import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import moment from "moment-timezone";
import {
  ButtonDropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";

import "./DateFilterElt.scss";

/*
Constants and helpers
*/

const CURRENT_TIME = moment.tz(moment.tz.guess(true));
const DEFAULT_OPTION_KEY = "notSet";

const DEFAULT_OPTIONS_DESCRIPTION = {
  [DEFAULT_OPTION_KEY]: {
    label: "Filter...",
    range: {},
  },
  today: {
    label: "Today",
    range: {
      timeEnd: moment(CURRENT_TIME).endOf("day"),
      timeStart: moment(CURRENT_TIME).startOf("day"),
    },
  },
  yesterday: {
    label: "Yesterday",
    range: {
      timeEnd: moment(CURRENT_TIME).subtract(1, "days").endOf("day"),
      timeStart: moment(CURRENT_TIME).subtract(1, "days").startOf("day"),
    },
  },
  last7days: {
    label: "Last 7 Days",
    range: {
      timeEnd: moment(CURRENT_TIME).endOf("day"),
      timeStart: moment(CURRENT_TIME).subtract(6, "days").startOf("day"),
    },
  },
  last30days: {
    label: "Last 30 Days",
    range: {
      timeEnd: moment(CURRENT_TIME).endOf("day"),
      timeStart: moment(CURRENT_TIME).subtract(29, "days").startOf("day"),
    },
  },
};

// Given an object 'optionsDescription' of the same form as
// DEFAULT_OPTIONS_DESCRIPTION and its key, returns a
// reactstrap DropdownItem
const keyToDropdownOption =
  ({ optionsDescription, onChangeDropdown }) =>
  (key) => {
    const elt = optionsDescription[key];
    const { label } = elt;

    const onClick = () => {
      onChangeDropdown(key);
    };

    return (
      <DropdownItem id={key} key={key} onClick={onClick}>
        {label}
      </DropdownItem>
    );
  };

// A helper component for reactstrap DropdownButton
const DropdownCustomLabel = ({ label }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
    }}
  >
    <div>{label}</div>

    <div>
      <i className="fas fa-caret-down" />
    </div>
  </div>
);

DropdownCustomLabel.propTypes = {
  label: PropTypes.string,
};

DropdownCustomLabel.defaultProps = {
  label: "",
};

const DateFilterElt = ({
  fieldName,
  optionsDescription = DEFAULT_OPTIONS_DESCRIPTION,
  setFilter,
  optionKey = DEFAULT_OPTION_KEY,
}) => {
  const [currentOptionKey, setCurrentOptionKey] = useState(DEFAULT_OPTION_KEY);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    setCurrentOptionKey(optionKey || DEFAULT_OPTION_KEY);
  }, [optionKey]);

  const onChangeDropdown = (key) => {
    // Update state
    setCurrentOptionKey(key);

    const { [key]: option } = optionsDescription;
    const { range } = option;

    // Update Redux
    // ***** create and action for this
    setFilter({ fieldName, filter: { value: { ...range }, key } });
  };

  const dropdownOptions = Object.keys(optionsDescription).map(
    keyToDropdownOption({ optionsDescription, onChangeDropdown })
  );

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const { label: dropdownLabel } = optionsDescription[currentOptionKey];

  return (
    <div>
      <ButtonDropdown
        className="resources-filter-dropdown"
        isOpen={dropdownOpen}
        toggle={toggleDropdown}
      >
        <DropdownToggle size="sm" color="link" outline>
          <DropdownCustomLabel label={dropdownLabel} />
        </DropdownToggle>

        <DropdownMenu className="resources-filter-dropdown-menu">
          {dropdownOptions}
        </DropdownMenu>
      </ButtonDropdown>
    </div>
  );
};

DateFilterElt.propTypes = {
  fieldName: PropTypes.string,
  optionKey: PropTypes.string,
  optionsDescription: PropTypes.shape({}),
  setFilter: PropTypes.func,
};

DateFilterElt.defaultProps = {
  fieldName: "",
  optionKey: DEFAULT_OPTION_KEY,
  optionsDescription: DEFAULT_OPTIONS_DESCRIPTION,
  setFilter: () => {},
};

export default DateFilterElt;
